import React from 'react';
import { t } from '../utils/i18n';
import Spacing from '../components/common/Spacing';
import SEO from '../components/head/SEO';
import { generatePath } from '../urls/generatePath';
import { Paths } from '../urls/Paths';
import { navbarLayout } from '../layouts/navbarLayout';
import H from '../components/elements/H';
import Breadcrumbs, { BreadcrumbItem } from '../components/head/Breadcrumbs';
import Container from '../components/grid/Container';
import HorizontalLine from '../components/common/HorizontalLine';
import {
  CmsHelpPageCategory,
  CmsHelpPageTopic,
  CmsMainHelpPage
} from '../integrations/contentful/types';
import HeadingWithCount from '../components/common/HeadingWithCount';
import HelpCategoryItem from '../components/help-pages/HelpCategoryItem';
import {
  fetchHelpPageCategories,
  fetchMainHelpPage
} from '../integrations/contentful/queries';
import HelpTopicLiftLink from '../components/help-pages/HelpTopicLiftLink';
import ContactCard from '../components/help-pages/ContactCard';
import Col from '../components/grid/Col';
import Row from '../components/grid/Row';
import DisplayOn from '../components/common/DisplayOn';
import SmallHelpCategoryItem from '../components/help-pages/SmallHelpCategoryItem';
import StickyCol from '../components/grid/StickyCol';
import { columnTopSpacing } from '../styles/theme';
import { TimPage } from './TimPage';

export const helpPageBreadcrumbItem: BreadcrumbItem = {
  name: t('Tuki'),
  path: Paths.DestinationList
};

interface HelpPageTopicWithCategory extends CmsHelpPageTopic {
  category: CmsHelpPageCategory;
}

interface HelpPageProps {
  helpPage?: CmsMainHelpPage;
  mostReadTopicsWithCategories: HelpPageTopicWithCategory[];
}

const HelpPage: TimPage<HelpPageProps> = ({
  helpPage,
  mostReadTopicsWithCategories
}) => {
  if (!helpPage) {
    return null;
  }
  const helpCategories = helpPage.fields.categories;
  return (
    <div data-cy="help-page">
      <SEO
        title={helpPage.fields.seoTitle || helpPage.fields.title}
        canonicalPath={generatePath(Paths.Help)}
        description={helpPage.fields.seoDescription || helpPage.fields.ingress}
      />
      <Breadcrumbs items={[helpPageBreadcrumbItem]} />

      <Container>
        <Row>
          <Col size={[24, 24, 7]}>
            <StickyCol contentTopOffset={columnTopSpacing.sideColumn}>
              <Spacing bottom={5}>
                <Spacing bottom={1}>
                  <H styling="h5" color="gray">
                    {t('Tietoa palvelusta')}
                  </H>
                </Spacing>
                <H styling="h2">
                  <strong>{helpPage.fields.title}</strong>
                </H>
              </Spacing>
              <DisplayOn large>
                <SmallHelpCategoryItem
                  path={Paths.Help}
                  text={t('Etusivu')}
                  selected
                />
                {helpCategories.map((category) => {
                  return (
                    <Spacing top={1} key={category.sys.id}>
                      <SmallHelpCategoryItem
                        path={Paths.HelpCategory}
                        slug={category.fields.slug}
                        text={category.fields.title}
                      />
                    </Spacing>
                  );
                })}
              </DisplayOn>
            </StickyCol>
          </Col>
          <Col size={[0, 0, 1]}></Col>
          <Col size={[24, 24, 16]}>
            <Spacing bottom={3} top={columnTopSpacing.contentColumn}>
              <H styling="h3" tag="div">
                {helpPage.fields.ingress}
              </H>
            </Spacing>
            <HorizontalLine />
            <Spacing top={5} bottom={7}>
              <H styling="h3">
                <strong>{t('Luetuimmat aiheet')}</strong>
              </H>
              <Spacing top={3}>
                {mostReadTopicsWithCategories.map((topic, index) => {
                  return (
                    <HelpTopicLiftLink
                      key={index}
                      topicTitle={topic.fields.title}
                      categorySlug={topic.category.fields.slug}
                    />
                  );
                })}
              </Spacing>
            </Spacing>
            <Spacing bottom={[11, 7, 7]}>
              <ContactCard />
            </Spacing>
            <DisplayOn small medium>
              <HorizontalLine />
            </DisplayOn>
            <Spacing top={[4, 6, 6]} bottom={4}>
              <HeadingWithCount
                icon="travel-question"
                text={t('Aihealueet')}
                renderCount={() => helpCategories.length}
                data-testid="category-count"
              />
            </Spacing>
            <Spacing bottom={30}>
              <Row>
                {helpCategories.map((category) => {
                  return (
                    <Col key={category.sys.id} size={[24, 12, 12]}>
                      <Spacing bottom={4}>
                        <HelpCategoryItem helpCategory={category} />
                      </Spacing>
                    </Col>
                  );
                })}
              </Row>
            </Spacing>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

HelpPage.getLayout = (page) =>
  navbarLayout(
    <DisplayOn small medium>
      <H styling="h4" tag="div">
        <strong>{t('Tietoa palvelusta')}</strong>
      </H>
    </DisplayOn>,
    page
  );

function indexCategoriesByTopicIds(categories: CmsHelpPageCategory[]) {
  const categoriesByTopicIds: Record<string, CmsHelpPageCategory> = {};
  categories.forEach((category) => {
    category.fields.topics.forEach((topic) => {
      categoriesByTopicIds[topic.sys.id] = category;
    });
  });
  return categoriesByTopicIds;
}

// Export for easier unit testing
export function pairCategoriesToTopics(
  topics: readonly CmsHelpPageTopic[],
  categories: CmsHelpPageCategory[]
) {
  const categoriesByTopicIds = indexCategoriesByTopicIds(categories);
  return topics
    .map((topic) => {
      return {
        ...topic,
        category: categoriesByTopicIds[topic.sys.id]
      };
    })
    .filter((topic) => {
      // Filter out topics which have no category set. It is technically possible to create a topic without
      // category in CMS and add it as most read topic. Displaying this kind of topic however is not possible in
      // the app since topics can be shown only in the category page. So lets ignore them here.
      return topic.category;
    });
}

HelpPage.getInitialProps = async function () {
  const [helpPage, categories] = await Promise.all([
    fetchMainHelpPage(),
    fetchHelpPageCategories()
  ]);
  const httpErrorCode = !helpPage ? 404 : undefined;
  let mostReadTopicsWithCategories: HelpPageTopicWithCategory[] = [];
  if (helpPage && helpPage.fields.mostReadTopics) {
    mostReadTopicsWithCategories = pairCategoriesToTopics(
      helpPage.fields.mostReadTopics,
      categories
    );
  }
  return {
    helpPage,
    mostReadTopicsWithCategories,
    httpErrorCode
  };
};

export default HelpPage;
